import React from "react";
import { Box, Typography } from "@mui/material";

export const MODEL_LINE_COLORS = [
  "#0097D7",
  "#26890d",
  "#F28C00",
  "#E2231A",
  "#7B5EA7",
  "#1F7AA8",
  "#E87722",
  "#43B02A",
];
export const OTHER_MODEL_COLOR = "#999999";
export const TOP_MODEL_COUNT = 5;

export const formatShortDate = (isoDate) => {
  const d = new Date(isoDate);
  return d.toLocaleDateString("en-AU", { month: "short", day: "numeric" });
};

// Pivots daily records (each with its own per-model usage_stats[]) into one row
// per date with a column per top-N model, plus an "All other" rollup. Pass
// topCount = Infinity to chart every model with no rollup (per-user views).
export const buildModelTrendData = (records, topCount = TOP_MODEL_COUNT) => {
  if (!records?.length) return { rows: [], modelKeys: [] };

  const totalByModel = {};
  records.forEach((record) => {
    (record.usage_stats ?? []).forEach((item) => {
      totalByModel[item.model] = (totalByModel[item.model] ?? 0) + (item.grossQuantity ?? 0);
    });
  });

  const topModels = Object.entries(totalByModel)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topCount)
    .map(([model]) => model);
  const hasOther = Object.keys(totalByModel).length > topModels.length;

  const rows = [...records]
    .sort((a, b) => new Date(a.usage_date) - new Date(b.usage_date))
    .map((record) => {
      // __amounts carries the real gross USD per model alongside the plotted AIC,
      // so the tooltip shows actual spend rather than assuming a $/AIC rate.
      const row = { date: formatShortDate(record.usage_date), __amounts: {} };
      topModels.forEach((model) => { row[model] = 0; row.__amounts[model] = 0; });
      if (hasOther) { row["All other"] = 0; row.__amounts["All other"] = 0; }

      (record.usage_stats ?? []).forEach((item) => {
        const key = topModels.includes(item.model) ? item.model : "All other";
        if (row[key] === undefined) return;
        row[key] += item.grossQuantity ?? 0;
        row.__amounts[key] += item.grossAmount ?? 0;
      });
      return row;
    });

  return { rows, modelKeys: hasOther ? [...topModels, "All other"] : topModels };
};

// Recharts' XAxis `interval` = how many ticks to skip between labels shown.
// Aim for roughly this many visible labels regardless of range length, so a
// 7-day view shows every day but a 90-day view doesn't collide into mush.
const TARGET_TICK_COUNT = 12;
export const getTickInterval = (pointCount) =>
  pointCount > TARGET_TICK_COUNT ? Math.ceil(pointCount / TARGET_TICK_COUNT) - 1 : 0;

export const ModelTrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const sorted = [...payload].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #e8e8e8",
        borderRadius: "8px",
        p: "10px 14px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#555", mb: 0.5 }}>
        {label}
      </Typography>
      {sorted.map((entry) => {
        const amount = entry.payload?.__amounts?.[entry.dataKey] ?? (entry.value ?? 0) * 0.01;
        return (
          <Typography key={entry.dataKey} sx={{ fontSize: "0.78rem", color: entry.color, fontWeight: 600 }}>
            {entry.dataKey}: ${amount.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      })}
      <Typography sx={{ fontSize: "0.68rem", color: "#aaa", mt: 0.5, fontStyle: "italic" }}>
        Gross spend, before pool discounts
      </Typography>
    </Box>
  );
};
